import { Link } from 'react-router-dom'
import { Clock, Sparkles } from 'lucide-react'
import {
  useShooterSubscription,
  SHOOTER_TIER_LABELS,
} from '@/features/payments/hooks/use-shooter-subscription'

/**
 * Persistent banner shown at the top of every authenticated page when
 * the shooter is on a free trial.  Displays the number of days remaining
 * and a CTA to upgrade.
 *
 * Renders nothing when:
 * - the subscription is still loading
 * - the user is not trialing (active paid, cancelled, etc.)
 * - no `currentPeriodEnd` is set
 */
export function TrialBanner() {
  const { subscription, tier, loading } = useShooterSubscription()

  if (loading) return null
  if (!subscription) return null
  if (subscription.status !== 'trialing') return null
  if (!subscription.currentPeriodEnd) return null

  const endDate = new Date(subscription.currentPeriodEnd)
  const now = new Date()
  const msPerDay = 1000 * 60 * 60 * 24
  const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / msPerDay))

  const tierLabel = SHOOTER_TIER_LABELS[tier] ?? tier

  // Colour shifts as trial nears expiry
  const isUrgent = daysRemaining <= 7
  const wrapperClass = isUrgent
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-zero-200 bg-zero-50 text-zero-900'
  const iconClass = isUrgent ? 'text-amber-500' : 'text-zero-500'
  const ctaClass = isUrgent
    ? 'bg-amber-600 hover:bg-amber-700 text-white'
    : 'bg-zero-600 hover:bg-zero-700 text-white'

  return (
    <div
      className={`mb-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 ${wrapperClass}`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Clock className={`h-4 w-4 flex-shrink-0 ${iconClass}`} />
        <p className="text-sm">
          <span className="font-semibold">
            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
          </span>{' '}
          remaining on your {tierLabel} trial.
          {isUrgent && ' Upgrade now to keep full access.'}
        </p>
      </div>
      <Link
        to="/pricing"
        className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${ctaClass}`}
      >
        <Sparkles className="h-3.5 w-3.5" />
        Upgrade
      </Link>
    </div>
  )
}
